import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import getFirstGridElement from 'editor/src/store/design/selector/getFirstGridElement';
import getSpreadPagesNames from 'editor/src/store/design/selector/getSpreadPagesNames';
import { useSelector } from 'editor/src/store/hooks';

import isSpreadNameAMonth from 'editor/src/util/isSpreadNameAMonth';

// Kebab-case → "Title Cased Words". Used as a last-resort label when no
// translation key matches, so raw kebab strings don't leak into the UI.
function humanizeKebab(raw: string): string {
  return raw
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function useSpreadTitle(
  spreadName: string,
  spreadIndex: number,
  focusedContent: { contentTitle?: string } | undefined,
) {
  const { t, i18n } = useTranslation();
  const { pageNames, gridDate } = useSelector(
    (state) => ({
      pageNames: getSpreadPagesNames(state, spreadIndex),
      gridDate: getFirstGridElement(state, spreadIndex)?.grid.gridDate,
    }),
    shallowEqual,
  );

  const translateSpreadTitle = (title: string) => {
    if (isSpreadNameAMonth(title) && gridDate) {
      const date = new Date(gridDate.year, gridDate.month, 1);
      const year = date.getFullYear();
      const month = date.toLocaleString(i18n.languageWithDash, {
        month: 'short',
      });
      return `${month} ${year}`;
    }

    const lookupKey = title.toLowerCase().replace('-embroidery', '').replaceAll(' ', '-');
    const spreadTitleTranslationKey = `editor-spread-name-${lookupKey}`;
    const translatedTitle = t(spreadTitleTranslationKey) as string;
    if (translatedTitle !== spreadTitleTranslationKey) {
      return translatedTitle;
    }
    return humanizeKebab(lookupKey);
  };

  if (focusedContent) {
    return translateSpreadTitle(focusedContent.contentTitle || spreadName);
  }

  if (pageNames && pageNames.length > 1) {
    return pageNames
      .map((pageName) => {
        const pageTitleTranslationKey = `editor-page-name-${pageName}`;
        const translatedPage = t(pageTitleTranslationKey) as string;

        if (translatedPage !== pageTitleTranslationKey) {
          return translatedPage;
        }
        return pageName;
      })
      .join(' - ');
  }

  return translateSpreadTitle(spreadName);
}

export default useSpreadTitle;
